import type { FunctionComponent } from "react";
import { PageError, PageErrorType } from "../../state/types";

import { useSelector } from "react-redux";
import { Alert, AlertTitle } from "@mui/material";
import { RootState } from "../../state";
import { replaceMessagingText } from "../../utilities/helperFuncs";

type Props = {
  error: PageError;
};

const ErrorAlert: FunctionComponent<Props> = (props) => {
  const { error } = props;
  const pageConfig = useSelector((state: RootState) => state.pageConfig);
  const errorHeading =
    pageConfig?.content?.errorHeading ?? "Your Invitation is Invalid or In Use";
  const invitationCancelled =
    pageConfig?.content?.invitationCancelled ??
    "Your invitation has been cancelled.";
  const participantNotExpected =
    pageConfig?.content?.participantNotExpected ??
    "Thank you for your interest! If you feel you have received this message in error, please contact your booking associate.";
  const duplicateConnection =
    pageConfig?.content?.duplicateConnection ??
    "You have signed into this service on a different device, {{osName}} {{osVersion}} {{deviceType}} on {{browserName}}";

  // A support code makes the three faults tellable apart in a screenshot. They
  // previously shared one message, so nobody could measure which one occurred.
  let errorBody = "";
  let supportCode = "";
  if (error.type === PageErrorType.InvitationCancelled) {
    errorBody = invitationCancelled;
    supportCode = "SD-CANCELLED";
  } else if (error.type === PageErrorType.InvitationInUse) {
    // `duplicateConnection` is the copy written for this case, but nothing ever
    // rendered it, so a guest whose link was taken over read the generic "not
    // expected" text instead. It names the other device, so use it only when
    // the server told us which device that was.
    const device = error.deviceInfo;
    errorBody = device
      ? replaceMessagingText(duplicateConnection, {
          browserName: device.browser?.name ?? "",
          deviceType: device.device?.type ?? "",
          osName: device.os?.name ?? "",
          osVersion: device.os?.version ?? "",
        })
      : participantNotExpected;
    supportCode = "SD-IN-USE";
  } else if (error.type === PageErrorType.InvitationInvalid) {
    errorBody = participantNotExpected;
    supportCode = "SD-NOT-FOUND";
  } else {
    errorBody = error.message ?? "An unexpected error occurred.";
    supportCode = "SD-UNEXPECTED";
  }

  return (
    <Alert
      className="fullWidth alert-margin"
      color="error"
      icon={false}
      severity="error"
      sx={{
        display: "block",
        textAlign: "center",
      }}
    >
      <AlertTitle sx={{ fontSize: "2rem" }}>{errorHeading}</AlertTitle>
      {errorBody}
      <div
        data-testid="error-support-code"
        style={{ fontSize: "0.75rem", marginTop: "0.75rem", opacity: 0.7 }}
      >
        {supportCode}
      </div>
    </Alert>
  );
};

export default ErrorAlert;
